import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { api } from "@/lib/api";

const LABEL = { si: "Sí", no: "No", na: "N/A" };
const RISK_RGB = { Bajo: [16, 185, 129], Medio: [245, 158, 11], Alto: [244, 63, 94] };

const toDataUrl = async (path, png = false) => {
  const { data } = await api.get(`/files/${path}`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
  const scale = Math.min(1, 900 / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = img.width * scale;
  c.height = img.height * scale;
  const ctx = c.getContext("2d");
  if (!png) { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, c.width, c.height); }
  ctx.drawImage(img, 0, 0, c.width, c.height);
  URL.revokeObjectURL(url);
  return { src: png ? c.toDataURL("image/png") : c.toDataURL("image/jpeg", 0.8), ratio: c.height / c.width };
};

export async function downloadAuditPdf(audit, logoPath) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  let logo = null;
  if (logoPath) { try { logo = await toDataUrl(logoPath, true); } catch { logo = null; } }
  doc.setFillColor(11, 15, 23);
  doc.rect(0, 0, W, 48, "F");
  let tx = 38;
  if (logo) {
    doc.setFillColor(255, 255, 255);
    doc.rect(10, 8, 40, 32, "F");
    const bw = 36, bh = 28;
    const w = Math.min(bw, bh / logo.ratio), h = w * logo.ratio;
    doc.addImage(logo.src, "PNG", 12 + (bw - w) / 2, 10 + (bh - h) / 2, w, h);
    tx = 58;
  } else {
    doc.setFillColor(245, 158, 11);
    doc.triangle(16, 30, 24, 16, 32, 30, "F");
  }
  doc.setTextColor(245, 241, 232);
  doc.setFont("helvetica", "bold").setFontSize(15).text(`Reporte de auditoría · ${audit.module_name ?? "Diagnóstico Exprés STPS"}`, tx, 17);
  doc.setFont("helvetica", "normal").setFontSize(10).text(audit.module_subtitle ?? "", tx, 23);
  doc.setFontSize(8.5).text("Vigía by Ascentra Consulting · Portal de cumplimiento empresarial", tx, 29);
  doc.text(`Folio: ${audit.id}`, tx, 34);
  if (audit.resolved) doc.setTextColor(16, 185, 129).text(`Estatus actualizado: ${audit.resolved} de ${audit.no} hallazgos resueltos`, tx, 40);

  doc.setTextColor(20, 20, 20).setFontSize(10);
  const date = new Date(audit.created_at).toLocaleString("es-MX");
  [["Empresa", audit.company], ["Centro de trabajo", audit.site], ["Auditor", audit.auditor], ["Fecha", date]].forEach(([k, v], i) => {
    doc.setFont("helvetica", "bold").text(`${k}:`, 16, 60 + i * 6);
    doc.setFont("helvetica", "normal").text(String(v), 52, 60 + i * 6);
  });

  const rgb = RISK_RGB[audit.risk] ?? RISK_RGB.Alto;
  doc.setDrawColor(...rgb).setLineWidth(1).rect(140, 54, 54, 30);
  doc.setFont("helvetica", "bold").setFontSize(26).setTextColor(...rgb).text(`${audit.score}%`, 167, 69, { align: "center" });
  doc.setFontSize(10).text(`Riesgo ${audit.risk}`, 167, 78, { align: "center" });

  doc.setTextColor(60, 60, 60).setFont("helvetica", "normal").setFontSize(9);
  doc.text(`Sí: ${audit.yes}   No: ${audit.no}   N/A: ${audit.na}   Resueltos: ${audit.resolved ?? 0}   ·   Cumplimiento = (Sí + resueltos) / (${audit.total} - N/A) x 100`, 16, 92);
  if (audit.resolved) doc.text(`Score inicial: ${audit.initial_score}% (riesgo ${audit.initial_risk})  >>  Score actual: ${audit.score}% (riesgo ${audit.risk})`, 16, 97);

  autoTable(doc, {
    startY: 102,
    head: [["Bloque", "Norma", "Cumplimiento"]],
    body: (audit.blocks ?? []).map((b) => [b.title, b.code, b.score == null ? "N/A" : `${b.score}%`]),
    headStyles: { fillColor: [11, 15, 23] },
    styles: { fontSize: 9 },
  });

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 8,
    head: [["#", "Reactivo", "Respuesta"]],
    body: (audit.answers ?? []).map((a, i) => [i + 1, a.text, a.answer === "no" && audit.resolutions?.[a.question_id] ? "No / Resuelto" : LABEL[a.answer]]),
    headStyles: { fillColor: [11, 15, 23] },
    styles: { fontSize: 9 },
    columnStyles: { 0: { cellWidth: 10 }, 2: { cellWidth: 30 } },
    didParseCell: (d) => {
      if (d.section !== "body" || d.column.index !== 2) return;
      if (d.cell.raw === "No") d.cell.styles.textColor = [244, 63, 94];
      if (d.cell.raw === "No / Resuelto") d.cell.styles.textColor = [16, 185, 129];
    },
  });

  const plan = audit.action_plan ?? [];
  if (plan.length) {
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 8,
      head: [["Prioridad", "Acción correctiva sugerida", "Estatus"]],
      body: plan.map((p) => [p.priority, `${p.action}\n(${p.question_id.toUpperCase()} · ${p.code})`, p.resolved ? `Resuelta ${new Date(p.resolved_at).toLocaleDateString("es-MX")}` : `Pendiente · máx. ${p.days} días`]),
      headStyles: { fillColor: [245, 158, 11], textColor: [11, 15, 23] },
      styles: { fontSize: 9 },
      columnStyles: { 0: { cellWidth: 22 }, 2: { cellWidth: 32 } },
      didParseCell: (d) => {
        if (d.section === "body" && d.column.index === 0) d.cell.styles.textColor = { "Crítica": [244, 63, 94], Alta: [217, 119, 6], Media: [14, 116, 144] }[d.cell.raw] ?? [20, 20, 20];
        if (d.section === "body" && d.column.index === 2) d.cell.styles.textColor = String(d.cell.raw).startsWith("Resuelta") ? [16, 185, 129] : [120, 120, 120];
      },
    });
  }

  const isPdf = (p) => p?.toLowerCase().endsWith(".pdf");
  const findings = (audit.answers ?? []).filter((a) => a.answer === "no" || a.photo_path);
  let cursor = doc.lastAutoTable.finalY;
  if (findings.length) {
    doc.addPage();
    doc.setFont("helvetica", "bold").setFontSize(14).setTextColor(20, 20, 20).text("Evidencia de hallazgos y cierre", 16, 20);
    let y = 30;
    for (const a of findings) {
      const res = audit.resolutions?.[a.question_id];
      const pair = [[a.photo_path, "Hallazgo", [244, 63, 94]], [isPdf(res?.photo_path) ? null : res?.photo_path, "Cierre", [16, 185, 129]]];
      const imgs = [];
      for (const [p, label, color] of pair) {
        if (!p) continue;
        try { imgs.push({ ...(await toDataUrl(p)), label, color }); } catch { /* skip unreadable image */ }
      }
      const notes = [];
      if (!a.photo_path) notes.push(["Hallazgo documental registrado en Plan de Acción.", [14, 116, 144]]);
      if (isPdf(res?.photo_path)) notes.push([`Cierre documental: documento PDF adjunto en la plataforma (${new Date(res.resolved_at).toLocaleDateString("es-MX")}).`, [16, 185, 129]]);
      const title = doc.splitTextToSize(`${a.question_id.toUpperCase()} · ${a.text}`, 178);
      const h = imgs.length ? Math.min(60, Math.max(...imgs.map((i) => 80 * i.ratio))) : 0;
      const block = title.length * 4 + notes.length * 5 + (imgs.length ? h + 12 : 0) + 8;
      if (y + block > 282) { doc.addPage(); y = 20; }
      doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(20, 20, 20).text(title, 16, y);
      y += title.length * 4;
      notes.forEach(([t, c]) => { doc.setFontSize(8.5).setTextColor(...c).text(t, 16, y + 1); y += 5; });
      if (imgs.length) {
        imgs.forEach((im, k) => {
          const x = 16 + k * 92;
          const ih = Math.min(h, 80 * im.ratio);
          doc.setFontSize(8).setTextColor(...im.color).text(im.label.toUpperCase(), x, y + 3);
          doc.addImage(im.src, "JPEG", x, y + 5, ih / im.ratio, ih);
        });
        y += h + 12;
      }
      y += 8;
    }
    cursor = y;
  }

  let sy = cursor + 12;
  if (sy + 80 > 282) { doc.addPage(); sy = 30; }
  doc.setFont("helvetica", "bold").setFontSize(12).setTextColor(20, 20, 20).text("Firmas de conformidad", 16, sy);
  doc.setFont("helvetica", "normal").setFontSize(8.5).setTextColor(90, 90, 90)
    .text(doc.splitTextToSize("Las partes firman de conformidad con los resultados, hallazgos y plan de acción contenidos en este reporte.", 178), 16, sy + 6);
  [[16, "Auditor responsable", audit.auditor], [114, "Encargado / responsable que recibe", ""]].forEach(([x, role, name]) => {
    const ly = sy + 42;
    doc.setDrawColor(40, 40, 40).setLineWidth(0.3).line(x, ly, x + 80, ly);
    doc.setFont("helvetica", "bold").setFontSize(9).setTextColor(20, 20, 20).text(role, x, ly + 5);
    doc.setFont("helvetica", "normal").setFontSize(9).text("Firma", x, ly + 10);
    doc.text(`Nombre: ${name || ""}`, x, ly + 20);
    doc.line(x + 14, ly + 21, x + 80, ly + 21);
    doc.text("Fecha:", x, ly + 30);
    doc.line(x + 12, ly + 31, x + 80, ly + 31);
  });

  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(8).setTextColor(130, 130, 130).text(`Vigía by Ascentra Consulting · Autodiagnóstico, no sustituye inspección oficial STPS · ${p}/${pages}`, W / 2, 290, { align: "center" });
  }
  doc.save(`${(audit.module_short ?? "auditoria").toLowerCase()}-${audit.company.replace(/\s+/g, "-").toLowerCase()}-${audit.id.slice(-6)}.pdf`);
}
